import { decodeGsxBlock } from "../../format/decoder";
import type { GsxMeta, SplatBlock } from "../../format/format";
import { NO_BLOCK } from "../../format/format";
import type { GsplatTransport } from "../../transport/types";

export interface SpatialProgress {
  inView: number;     // # nodes intersecting frustum
  resident: number;   // # blocks currently on GPU
  inFlight: number;   // # blocks being fetched
  splats: number;     // total resident splats
}

export interface SpatialLoaderOptions {
  concurrency?: number;
  /** Hard cap on resident blocks. */
  maxResident?: number;
  /**
   * Hard cap on resident SPLATS — the renderer's real constraint. Without it
   * the want set is chosen by block count alone, so a scene whose selected
   * blocks are fatter than average asks for more splats than the GPU holds;
   * the renderer then evicts to make room for blocks that are still wanted,
   * and loader and renderer fight each frame (refetch → inflate → evict).
   */
  splatBudget?: number;
  /** Resident blocks dropped from in-view set are kept this long before evicting. */
  evictionDelayMs?: number;
  /**
   * Nodes at level <= baseLevelMax are ALWAYS wanted (frustum cull skipped).
   * They form the "foundation" — coarse samples covering the whole scene so
   * users always see SOMETHING regardless of view direction. Default 2.
   */
  baseLevelMax?: number;
  onProgress?: (p: SpatialProgress) => void;
  onError?: (err: { stage: string; nodeId: number; message: string; cause?: string }) => void;
}

interface RendererSink {
  has(nodeId: number): boolean;
  touch(nodeId: number): void;
  addNode(nodeId: number, block: SplatBlock): boolean;
  removeNode(nodeId: number): void;
  residentNodes(): number[];
  bumpFrame(): void;
}

/**
 * Camera state needed for frustum cull + priority. Provided by the host
 * (Three.js adapter or sandbox). The engine doesn't depend on any specific
 * camera class.
 */
export interface CullCamera {
  /** Eye world position. */
  eye: [number, number, number];
  /** view * proj — used to derive the 6 frustum planes. Column-major. */
  viewProj: Float32Array;
}

interface FrustumPlane {
  a: number; b: number; c: number; d: number;
}

/**
 * Spatial-cull loader for multi-block GSX files (stratified octree LOD).
 *
 * Per update():
 *   1. Build 6 frustum planes from camera viewProj.
 *   2. For each node with a block: foundation (level ≤ baseLevelMax) is always
 *      wanted; deeper levels are frustum-culled. Priority = tier + level + dist.
 *   3. Top-K under maxResident becomes the "want" set.
 *   4. For wanted-but-not-resident: enqueue fetch (concurrency cap).
 *   5. For resident-but-not-wanted: schedule eviction after evictionDelayMs.
 */
export class SpatialLoader {
  private cacheKey: string; // unused for now; reserved for per-block IDB cache later
  private transport: GsplatTransport;
  private renderer: RendererSink;
  private opts: Required<Omit<SpatialLoaderOptions, "onProgress" | "onError">> &
    Pick<SpatialLoaderOptions, "onProgress" | "onError">;
  private meta: GsxMeta | null = null;
  private aborted = false;
  /**
   * Block index entries carry payload BYTES, not splat counts — the count
   * only appears inside the payload. The bit layout is the same for every
   * block, so one ratio over the whole file estimates any block's splats.
   */
  private splatsPerByte = 0;
  /**
   * True splat count per node, filled in as blocks are decoded. The byte
   * estimate is systematically low for deep blocks — they have tight AABBs
   * and compress harder, so they hold more splats per byte than the file
   * average — which is exactly the direction that overfills the GPU.
   */
  private splatsByNode = new Map<number, number>();

  private inFlight = new Set<number>();
  private failedAt = new Map<number, number>(); // nodeId → epoch ms retry-after
  private evictAt = new Map<number, number>();  // nodeId → epoch ms eviction-deadline

  constructor(
    cacheKey: string,
    transport: GsplatTransport,
    renderer: RendererSink,
    opts: SpatialLoaderOptions = {},
  ) {
    this.cacheKey = cacheKey;
    this.transport = transport;
    this.renderer = renderer;
    this.opts = {
      concurrency: opts.concurrency ?? 16,
      maxResident: opts.maxResident ?? 500,
      splatBudget: opts.splatBudget ?? Infinity,
      evictionDelayMs: opts.evictionDelayMs ?? 2000,
      baseLevelMax: opts.baseLevelMax ?? 2,
      onProgress: opts.onProgress,
      onError: opts.onError,
    };
    // Suppress unused-var warning until per-block IDB caching is implemented.
    void this.cacheKey;
  }

  async loadMeta(): Promise<GsxMeta> {
    const meta = await this.transport.getMeta();
    this.meta = meta;
    let bytes = 0;
    for (const b of meta.blocks) bytes += b.sizeUncompressed;
    this.splatsPerByte = bytes > 0 ? meta.header.splatCount / bytes : 0;
    return meta;
  }

  hasMeta(): boolean {
    return this.meta !== null;
  }

  getMeta(): GsxMeta | null {
    return this.meta;
  }

  abort(): void {
    this.aborted = true;
  }

  /** Per-frame: cull, enqueue fetches, schedule evictions. */
  update(camera: CullCamera, viewMatrix: Float32Array, projMatrix: Float32Array): void {
    if (!this.meta || this.aborted) return;
    this.renderer.bumpFrame();
    const viewProj = mat4Multiply(projMatrix, viewMatrix);
    const planes = extractFrustumPlanes(viewProj);
    const eye = camera.eye;
    const baseMax = this.opts.baseLevelMax;
    const candidates: Array<{ nodeId: number; priority: number; isBase: boolean }> = [];
    const nodes = this.meta.nodes;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]!;
      if (node.blockId === NO_BLOCK) continue;
      const isBase = node.level <= baseMax;
      if (!isBase && !aabbInFrustum(node.aabbMin, node.aabbMax, planes)) continue;
      const cx = (node.aabbMin[0] + node.aabbMax[0]) * 0.5;
      const cy = (node.aabbMin[1] + node.aabbMax[1]) * 0.5;
      const cz = (node.aabbMin[2] + node.aabbMax[2]) * 0.5;
      const dx = cx - eye[0];
      const dy = cy - eye[1];
      const dz = cz - eye[2];
      const distSq = dx * dx + dy * dy + dz * dz;
      // Screen-space center bias: project AABB center to NDC; the closer to
      // viewport center (0,0) the higher the bonus. Within-frustum non-base
      // blocks always have clipW > 0; the guard handles base / behind-camera.
      let centerBonus = 0;
      const clipX = viewProj[0]! * cx + viewProj[4]! * cy + viewProj[8]!  * cz + viewProj[12]!;
      const clipY = viewProj[1]! * cx + viewProj[5]! * cy + viewProj[9]!  * cz + viewProj[13]!;
      const clipW = viewProj[3]! * cx + viewProj[7]! * cy + viewProj[11]! * cz + viewProj[15]!;
      if (clipW > 1e-6) {
        const ndcX = clipX / clipW;
        const ndcY = clipY / clipW;
        const r = Math.min(1, Math.sqrt(ndcX * ndcX + ndcY * ndcY) / Math.SQRT2);
        centerBonus = (1 - r) * 5e6;  // 0..5e6; rivals levelBonus magnitude
      }
      const tier = isBase ? 1e12 : 0;
      const levelBonus = (10 - node.level) * 1e6;
      candidates.push({
        nodeId: i,
        priority: tier + levelBonus + centerBonus - distSq,
        isBase,
      });
    }
    candidates.sort((a, b) => b.priority - a.priority);

    // Take candidates in priority order until either ceiling is reached. The
    // splat ceiling is the one the renderer actually enforces; stopping here
    // means we never ask for a block that can only arrive by evicting another
    // block we still want.
    const budget = this.opts.splatBudget;
    const wantSet = new Set<number>();
    let keep = 0;
    let wantSplats = 0;
    for (let i = 0; i < candidates.length && keep < this.opts.maxResident; i++) {
      const nodeId = candidates[i]!.nodeId;
      const node = nodes[nodeId]!;
      const est = this.splatsByNode.get(nodeId)
        ?? Math.ceil((this.meta.blocks[node.blockId]?.sizeUncompressed ?? 0) * this.splatsPerByte * 1.2);
      if (keep > 0 && wantSplats + est > budget) break;
      wantSet.add(nodeId);
      wantSplats += est;
      keep = i + 1;
    }

    // Phase 1: hysteretic eviction.
    const now = Date.now();
    for (const nodeId of this.renderer.residentNodes()) {
      if (wantSet.has(nodeId)) {
        this.evictAt.delete(nodeId);
        continue;
      }
      let deadline = this.evictAt.get(nodeId);
      if (deadline === undefined) {
        deadline = now + this.opts.evictionDelayMs;
        this.evictAt.set(nodeId, deadline);
      }
      if (deadline <= now) {
        this.renderer.removeNode(nodeId);
        this.evictAt.delete(nodeId);
      }
    }

    // Phase 2: touch resident wanted; enqueue missing wanted.
    let resident = 0;
    for (let i = 0; i < keep; i++) {
      const nodeId = candidates[i]!.nodeId;
      if (!wantSet.has(nodeId)) continue;
      if (this.renderer.has(nodeId)) {
        this.renderer.touch(nodeId);
        resident++;
        continue;
      }
      if (this.inFlight.has(nodeId)) continue;
      const retryAt = this.failedAt.get(nodeId);
      if (retryAt !== undefined && retryAt > now) continue;
      if (this.inFlight.size >= this.opts.concurrency) continue;
      void this.fetchNode(nodeId);
    }

    let splats = 0;
    for (const nodeId of this.renderer.residentNodes()) {
      const node = this.meta.nodes[nodeId];
      if (node) {
        splats += this.splatsByNode.get(nodeId)
          ?? Math.ceil((this.meta.blocks[node.blockId]?.sizeUncompressed ?? 0) * this.splatsPerByte);
      }
    }
    this.opts.onProgress?.({
      inView: candidates.length,
      resident,
      inFlight: this.inFlight.size,
      splats,
    });
  }

  private async fetchNode(nodeId: number): Promise<void> {
    if (!this.meta) return;
    const node = this.meta.nodes[nodeId];
    if (!node || node.blockId === NO_BLOCK) return;
    const entry = this.meta.blocks[node.blockId];
    if (!entry || entry.sizeCompressed === 0) return;
    if (this.inFlight.has(nodeId)) return;
    this.inFlight.add(nodeId);
    let stage = "fetch";
    try {
      const bytes = await this.transport.getBlockBytes(node.blockId, {
        offset: entry.offset,
        sizeCompressed: entry.sizeCompressed,
      });
      if (this.aborted) return;
      stage = "decode";
      const block = await decodeGsxBlock(bytes, this.meta.header, entry);
      if (this.aborted) return;
      this.splatsByNode.set(nodeId, block.splatCount);
      stage = "addNode";
      const ok = this.renderer.addNode(nodeId, block);
      if (!ok) {
        // The GPU is full of blocks we still want. Retrying on a timer just
        // re-reads and re-inflates this block forever; the want set now knows
        // its true size and will stop asking once the budget is recomputed.
        this.failedAt.set(nodeId, Date.now() + 30_000);
        return;
      }
      this.failedAt.delete(nodeId);
    } catch (err) {
      const prior = this.failedAt.get(nodeId) ?? 0;
      const elapsed = Date.now() - (prior || Date.now());
      const next = Math.min(60_000, Math.max(1000, elapsed * 2));
      this.failedAt.set(nodeId, Date.now() + next);
      const msg = err instanceof Error ? err.message : String(err);
      const cause =
        err instanceof Error && err.cause instanceof Error ? err.cause.message : undefined;
      this.opts.onError?.({ stage, nodeId, message: msg, cause });
    } finally {
      this.inFlight.delete(nodeId);
    }
  }
}

function extractFrustumPlanes(viewProj: Float32Array): FrustumPlane[] {
  const m = (row: number, col: number) => viewProj[col * 4 + row]!;
  return [
    { a: m(3, 0) + m(0, 0), b: m(3, 1) + m(0, 1), c: m(3, 2) + m(0, 2), d: m(3, 3) + m(0, 3) },
    { a: m(3, 0) - m(0, 0), b: m(3, 1) - m(0, 1), c: m(3, 2) - m(0, 2), d: m(3, 3) - m(0, 3) },
    { a: m(3, 0) + m(1, 0), b: m(3, 1) + m(1, 1), c: m(3, 2) + m(1, 2), d: m(3, 3) + m(1, 3) },
    { a: m(3, 0) - m(1, 0), b: m(3, 1) - m(1, 1), c: m(3, 2) - m(1, 2), d: m(3, 3) - m(1, 3) },
    { a: m(3, 0) + m(2, 0), b: m(3, 1) + m(2, 1), c: m(3, 2) + m(2, 2), d: m(3, 3) + m(2, 3) },
    { a: m(3, 0) - m(2, 0), b: m(3, 1) - m(2, 1), c: m(3, 2) - m(2, 2), d: m(3, 3) - m(2, 3) },
  ];
}

function aabbInFrustum(
  aabbMin: [number, number, number],
  aabbMax: [number, number, number],
  planes: FrustumPlane[],
): boolean {
  for (const p of planes) {
    const px = p.a >= 0 ? aabbMax[0] : aabbMin[0];
    const py = p.b >= 0 ? aabbMax[1] : aabbMin[1];
    const pz = p.c >= 0 ? aabbMax[2] : aabbMin[2];
    if (p.a * px + p.b * py + p.c * pz + p.d < 0) return false;
  }
  return true;
}

function mat4Multiply(a: Float32Array, b: Float32Array): Float32Array {
  const out = new Float32Array(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let v = 0;
      for (let k = 0; k < 4; k++) v += a[k * 4 + row]! * b[col * 4 + k]!;
      out[col * 4 + row] = v;
    }
  }
  return out;
}
